// 상점 정산명세서 화면 공통 — 월 목록·제목·표시 항목.
// 금액 계산은 utils/settlementNetting.util.js(오퍼레이터 원본 복사)가 맡는다.
import { buildStatementBreakdown } from '../../utils/settlementNetting.util';

export function mobileListPath(storeNo) {
  return `/mobile/${storeNo}/settlement`;
}

/** 서버의 공개 시작 판매월 기본값과 같다(STORE_STATEMENT_OPEN_SALE_FROM). */
export const STATEMENT_OPEN_SALE_MONTH = '2026-08';

/** 주소에 쓰는 정산월의 최소값(공개 시작 판매월 + 1). */
const STATEMENT_MIN_PAYOUT_MONTH = '2026-09';

/** 지급예정일 문구의 일자. 오퍼레이터 명세서 문구와 같은 규칙이며 여기에만 둔다. */
const PAYOUT_DUE_DAY = 15;

/** 오퍼레이터 명세서와 같은 표기. 금액은 수수료율로 계산하지 않고 buildStatementBreakdown 값을 쓴다. */
const FEE_RATE_LABEL = '5.5%';

export const MESSAGES = {
  loading: '불러오는 중...',
  empty: '정산 내역이 없습니다.',
  inProgress: '정산이 진행 중입니다.',
  forbidden: '정산명세서를 볼 권한이 없습니다.',
  mismatch: '다른 상점의 정산명세서는 볼 수 없습니다.',
  error: '정산명세서를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
};

const YM_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;

/** 주소의 정산월이 YYYY-MM 형식이고 최소값 이후인지. */
export function isValidPayoutMonth(ym) {
  return YM_PATTERN.test(ym || '') && ym >= STATEMENT_MIN_PAYOUT_MONTH;
}

export function shiftMonth(ym, delta) {
  const [, y, m] = YM_PATTERN.exec(ym);
  const total = Number(y) * 12 + Number(m) - 1 + delta;
  const month = (total % 12) + 1;
  return `${Math.floor(total / 12)}-${String(month).padStart(2, '0')}`;
}

/** 한국 시간 기준 이번 달(YYYY-MM). 기기 시간대와 무관하다. */
export function currentMonthKst(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now);
  const pick = type => parts.find(p => p.type === type).value;
  return `${pick('year')}-${pick('month')}`;
}

/** 공개 시작 판매월부터 직전 달까지의 판매월, 최신순. */
export function buildSaleMonths(now = new Date()) {
  const months = [];
  let month = shiftMonth(currentMonthKst(now), -1);
  while (month >= STATEMENT_OPEN_SALE_MONTH) {
    months.push(month);
    month = shiftMonth(month, -1);
  }
  return months;
}

/** 정산월 = 판매월 + 1 */
export function toPayoutMonth(saleMonth) {
  return shiftMonth(saleMonth, 1);
}

/** 판매월 = 정산월 − 1. API 호출에 쓴다. */
export function toSaleMonth(payoutMonth) {
  return shiftMonth(payoutMonth, -1);
}

/** "2026-11" → "2026년 11월" */
export function formatMonthLabel(ym) {
  const [, y, m] = YM_PATTERN.exec(ym);
  return `${y}년 ${Number(m)}월`;
}

/** "2026-11" → "2026.11" */
export function formatMonthDot(ym) {
  return ym.replace('-', '.');
}

export function statementTitle(saleMonth) {
  return `내기프트 ${formatMonthLabel(toPayoutMonth(saleMonth))} 정산명세서`;
}

export function payoutDueLabel(payoutMonth) {
  return `${formatMonthLabel(payoutMonth)} ${PAYOUT_DUE_DAY}일 이내`;
}

export function formatWon(value) {
  return `${(value || 0).toLocaleString('ko-KR')}원`;
}

export function formatCount(value) {
  return value == null ? '' : `${value.toLocaleString('ko-KR')}건`;
}

export function isEmptyStatement(data) {
  return !(data?.payouts?.length > 0);
}

/** API 응답 → 화면 표시 항목. 모바일·PC가 같은 값을 쓴다. */
export function buildStatementView(data) {
  const s = data.sales ?? {};
  const { total } = buildStatementBreakdown(data.payouts ?? []);

  const settlementRows = [
    { key: 'C', sign: '+', label: '기프트 사용 금액 (C)', amount: total.gross, count: total.usedCount ?? 0 },
    { key: 'D', sign: '+', label: '바우처 사용 금액 (D)', amount: total.voucher, count: s.voucherCount ?? 0 },
    { key: 'E', sign: '+', label: '지류쿠폰 사용 금액 (E)', amount: total.coupon, count: s.couponCount ?? 0 },
  ];
  if (total.offlineCoupon) {
    settlementRows.push({
      key: 'E-offline',
      isSub: true,
      label: '그중 매장 쿠폰',
      amount: total.offlineCoupon,
      count: total.offlineCouponCount ?? 0,
    });
  }
  settlementRows.push({ key: 'F', sign: '−', label: `내기프트 수수료 (F, ${FEE_RATE_LABEL})`, amount: total.fee });

  return {
    payoutAmount: total.payout,
    payoutDue: payoutDueLabel(data.payoutMonths.to),
    storeName: data.storeName,
    adminId: data.adminId,
    usagePeriod: formatMonthLabel(data.saleMonths.from),
    settlementRows,
    formula: 'C + D + E − F',
  };
}
